import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminAlertText,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminFormSecondaryAction,
  AdminHeaderAction,
  AdminLinkAction,
  AdminPanelTitle,
  AdminSupportingText,
  AdminTabButton,
  AdminTableBody,
  AdminTableHeader,
  AdminTableMeta,
  adminFormTextareaClasses,
  ADMIN_DETAIL_MAIN_HEADER_CLASS,
  ADMIN_DETAIL_TABLE_CLASS,
  adminDetailTabContentClassName,
} from '../shared/adminUx';
import ConfigurationListPaginationFooter from '../../../components/AdminDashboard/Configuration/ConfigurationListPaginationFooter';
import { getDetailTablePagination } from '../../../utils/configurationTableUx';
import { ACTION_POPUP_CONTENT_WIDTH_CLASS } from '../../../components/popup/popupLayoutSpec';
import {
  getComplianceCaseOverride,
  patchComplianceCaseOverride,
} from '../../../utils/complianceCaseSession';
import {
  DEFAULT_DECISION_CHECK_LABELS,
  REJECTION_BASIS_OPTIONS,
  createDefaultCaseActions,
} from '../../../data/complianceConfiguration';
import { defaultFormInputsForItem } from '../../../data/licensingCredentialRecords';
import {
  COMPLIANCE_QUEUE_ROUTE,
  CURRENT_STAFF_MEMBER,
  decisionIdFromCaseId as queueDecisionIdFromCaseId,
  findQueueCase,
  findQueueSpecSample,
  formatAssignedDisplay as formatQueueAssignedDisplay,
  isDuringReview,
  markQueueHandoffSuccess,
  markQueueTransferred,
  upsertQueueCase,
} from '../../../data/complianceQueueSpec';
import { handoffQueueCaseToArchive } from '../../../data/complianceArchiveSpec';
import { recordCaseActivity } from '../../../data/changeHistoryAudit';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'submission', label: 'Submission' },
  { id: 'review', label: 'Decision' },
  { id: 'notes', label: 'Internal Notes' },
  { id: 'logs', label: 'Audit Log' },
];

const DEFAULT_CASE_ACTIONS = createDefaultCaseActions();
const QUEUE_REJECTION_BASIS = [...REJECTION_BASIS_OPTIONS];

const FILE_ACTION_GRID = 'minmax(0, 2fr) minmax(72px, auto)';
const SUBMISSION_HISTORY_GRID =
  'minmax(0, 1.2fr) minmax(0, 1.2fr) minmax(0, 1.2fr) minmax(0, 1.2fr) minmax(72px, auto)';
const NOTES_TABLE_GRID =
  'minmax(0, 2.2fr) minmax(0, 1fr) minmax(0, 1fr) minmax(88px, auto)';
const AUDIT_LOG_GRID = 'minmax(0, 1.15fr) minmax(0, 1fr) minmax(0, 1.8fr) minmax(0, 1.1fr)';

const APPLIED_RULE_BY_SOURCE_ID = {
  'LIC-0002': 'COM-0001',
  'LIC-0001': 'COM-0002',
  'BDG-0001': 'COM-0003',
  'TKT-0001': 'COM-0004',
};

const appliedRuleIdForSource = (itemId, itemName) => {
  if (APPLIED_RULE_BY_SOURCE_ID[itemId]) return APPLIED_RULE_BY_SOURCE_ID[itemId];
  if (itemName === 'Plumbing Licence') return 'COM-0001';
  if (itemName === 'Electrical Licence' || /electrical/i.test(itemName || '')) return 'COM-0002';
  if (/first aid/i.test(itemName || '')) return 'COM-0003';
  if (itemName === 'White Card') return 'COM-0004';
  return null;
};

const queueTypeToLbtType = (complianceType) => {
  if (complianceType === 'Badge') return 'badge';
  if (complianceType === 'Ticket') return 'trade_ticket';
  return 'licence';
};

const OPTIONAL_SUBMISSION_FIELDS = new Set([
  'Endorsements',
  'Licence Conditions / Restrictions',
  'Licence Variation',
  'Scope of Work',
  'Conditions / Restrictions',
  'Training Provider / RTO',
  'RTO Code',
  'Course / Unit Name',
  'Course / Unit Code',
  'Course / Unit Name(s)',
  'Course / Unit Code(s)',
  'Plant / Equipment Type',
  'Class / Level',
  'Certificate / Reference Number',
  'Check / Reference Number',
  'Cover Details',
  'Cover Amount',
]);

const submissionValueForField = (label, { sourceId, itemName, holderName }) => {
  const isPlumbing = sourceId === 'LIC-0002' || itemName === 'Plumbing Licence';

  if (label === 'Holder Name' || label === 'Policyholder / Insured Name') {
    return holderName || '—';
  }
  if (OPTIONAL_SUBMISSION_FIELDS.has(label) && !isPlumbing) return '—';

  if (!isPlumbing) {
    if (label === 'Issue Date' || label === 'Expiry Date' || label === 'Cover Start Date' || label === 'Cover End Date') {
      return '—';
    }
    return '—';
  }

  switch (label) {
    case 'Licence Number':
      return 'LIC-884201';
    case 'Issuing Authority':
    case 'Issuing Organisation':
      return 'Victorian Building Authority';
    case 'Jurisdiction':
      return 'Victoria';
    case 'Licence Class':
      return 'Plumbing Contractor';
    case 'Licence Category':
      return 'Contractor';
    case 'Issue Date':
      return formatDate('2025-03-12');
    case 'Expiry Date':
      return formatDate('2026-03-12');
    case 'Scope of Work':
      return 'Plumbing work';
    case 'Endorsements':
    case 'Licence Conditions / Restrictions':
    case 'Licence Variation':
      return '—';
    default:
      return '—';
  }
};

const buildSubmissionFields = ({ complianceType, complianceItem, sourceId, holderName }) => {
  const inputs = defaultFormInputsForItem(
    complianceItem,
    queueTypeToLbtType(complianceType)
  );
  return inputs
    .filter((input) => input.fieldControl !== 'File upload')
    .map((input) => ({
      label: input.fieldLabel,
      value: submissionValueForField(input.fieldLabel, {
        sourceId,
        itemName: complianceItem,
        holderName,
      }),
    }));
};

const OPTIONAL_MANDATORY_CHECKS = new Set(['State register checked']);

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  const datePart = date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  const timePart = date.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true });
  return `${datePart}, ${timePart}`;
};

const formatAssignedDisplay = (raw) => formatQueueAssignedDisplay(raw);

const addCalendarDays = (value, days) => {
  const date = new Date(value);
  date.setDate(date.getDate() + days);
  return date.toISOString();
};

const recordedTimeZone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || 'Australia/Melbourne';

const decisionIdFromCaseId = (caseId) => queueDecisionIdFromCaseId(caseId);

const recordIdFromCaseId = (caseId) => {
  const digits = String(caseId || '').replace(/\D/g, '');
  return `CR-${(digits || '0000').padStart(6, '0').slice(-6)}`;
};

const normalizeCaseType = (raw) => {
  if (!raw) return 'New';
  if (raw === 'New Submission') return 'New';
  return raw;
};

const normalizeCaseStatus = (raw) => {
  if (!raw) return 'New';
  if (raw === 'Validation Pending') return 'New';
  if (raw === 'Information Requested' || raw === 'Awaiting Information') return 'Under Review';
  return raw;
};

const relatedRecordPath = (value, previousCaseId) => {
  if (!value || value === '—') return null;
  const id = String(value);
  if (id.startsWith('ST-')) return `/admin/support/tickets/${encodeURIComponent(id)}`;
  if (id.startsWith('APL-') || id.startsWith('APP-')) {
    return `/admin/appeals/${encodeURIComponent(id)}`;
  }
  if (id.startsWith('CMP-DEC')) {
    return previousCaseId
      ? `/admin/verification/archive/${encodeURIComponent(previousCaseId)}`
      : null;
  }
  if (id.startsWith('CMP-')) return `/admin/verification/archive/${encodeURIComponent(id)}`;
  return null;
};

const DetailField = ({ label, value }) => (
  <div className="space-y-1">
    <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
    <AdminDetailFieldValue>
      {value != null && value !== '' ? value : '—'}
    </AdminDetailFieldValue>
  </div>
);

const LinkedIdField = ({ label, value, onOpen }) => (
  <div className="space-y-1">
    <AdminDetailFieldLabel className="mb-1">{label}</AdminDetailFieldLabel>
    {value ? (
      <AdminDetailFieldValue>
        {onOpen ? (
          <AdminLinkAction type="button" onClick={() => onOpen(value)}>
            {value}
          </AdminLinkAction>
        ) : (
          value
        )}
      </AdminDetailFieldValue>
    ) : (
      <AdminDetailFieldValue>—</AdminDetailFieldValue>
    )}
  </div>
);

const buildDefaultReviewChecks = (configuredLabels, { completed = false } = {}) => {
  const labels =
    Array.isArray(configuredLabels) && configuredLabels.length > 0
      ? configuredLabels
      : DEFAULT_DECISION_CHECK_LABELS;
  return labels.map((label, index) => {
    const mandatory = !OPTIONAL_MANDATORY_CHECKS.has(label);
    return {
      id: `check-${index + 1}`,
      label,
      mandatory,
      checked: completed ? mandatory : false,
    };
  });
};

const emptyFinalisation = () => [
  {
    item: 'Business Record Action',
    result: '—',
    relatedRecord: '—',
    lastAttempt: null,
    completedOn: null,
    status: '—',
  },
  {
    item: 'Archive Handoff',
    result: '—',
    relatedRecord: '—',
    lastAttempt: null,
    completedOn: null,
    status: '—',
  },
];

const buildCaseDetail = (complianceId, queueDocument) => {
  const id = complianceId || queueDocument?.compliance_id || 'CMP-004281';
  const specSample = findQueueSpecSample(id);
  const doc = { ...(specSample || {}), ...(queueDocument || {}) };
  const complianceType = doc.compliance_type || doc.category || 'Licence';
  const complianceItem = doc.compliance_item || doc.document_name || 'Plumbing Licence';
  const complianceItemId = doc.compliance_item_id || 'LIC-0002';
  const caseType = normalizeCaseType(doc.case_type || (id === 'CMP-004281' ? 'Renewal' : 'New'));
  const caseStatus = normalizeCaseStatus(
    doc.case_status || doc.verification_status || (id === 'CMP-004283' ? 'New' : 'Under Review')
  );
  const sourceTradeCategory =
    doc.trade_category ||
    (complianceItem === 'White Card' ||
    complianceItem === 'First Aid' ||
    complianceItem === 'Industry Membership' ||
    complianceItem === 'Working with Children Check' ||
    complianceItem === 'Working at Heights' ||
    complianceItem === 'Confined Spaces'
      ? 'All Trades'
      : complianceItem === 'Electrical Licence'
        ? 'Electrical'
        : 'Plumbing');
  const businessTradeCategory = doc.business_trade_category || sourceTradeCategory;
  const supportTeamMember = formatAssignedDisplay(doc.support_team_member ?? doc.assigned_to);
  const businessName = doc.business_name || 'Apex Plumbing';
  const businessId = doc.business_id || 'BUS-001284';
  const accountHolderLegalName = doc.account_holder_legal_name || null;
  const abn = doc.abn || null;
  const accountStatus = doc.account_status || 'Active';
  const submittedAt = doc.submitted_at || '2026-04-03T09:00:00+10:00';
  const lastUpdated = doc.last_updated || '2026-04-03T10:05:00+10:00';
  const expiryApplies =
    complianceType === 'Licence' || complianceType === 'Licensed Trade' || caseType === 'Renewal';
  const isRenewalOrReplacement = caseType === 'Renewal' || caseType === 'Replacement';
  const started = caseStatus !== 'New';
  const decided = caseStatus === 'Decided';
  const infoRequested = Boolean(doc.information_request || doc.hasInformationRequest);
  const responsePeriodDays = DEFAULT_CASE_ACTIONS.askMoreInfo.responsePeriodDays || 14;
  const infoAvailableAt = infoRequested ? lastUpdated : null;
  const infoDueAt = infoAvailableAt ? addCalendarDays(infoAvailableAt, responsePeriodDays) : null;

  const submissionFields = buildSubmissionFields({
    complianceType,
    complianceItem,
    sourceId: complianceItemId,
    holderName: accountHolderLegalName,
  });

  const currentRecord = isRenewalOrReplacement
    ? {
        businessComplianceRecordId: 'CR-003981',
        recordStatus: 'Active',
        effectiveFrom: '2025-03-15',
        expiryDate: '2026-03-12',
      }
    : null;

  const auditLog = [
    {
      id: 'LOG-001',
      dateTime: submittedAt,
      event: 'Case received',
      details: `System created ${id} as New.`,
      relatedRecord: '—',
    },
  ];
  if (supportTeamMember !== 'Unassigned') {
    auditLog.push({
      id: 'LOG-002',
      dateTime: submittedAt,
      event: 'Assigned',
      details: `System assigned ${supportTeamMember}.`,
      relatedRecord: '—',
    });
  }
  if (infoRequested) {
    auditLog.push({
      id: 'LOG-004',
      dateTime: lastUpdated,
      event: 'Information requested',
      details: `${supportTeamMember} requested more information. Linked Support Ticket ST-000185. Due ${formatDateTime(infoDueAt)}.`,
      relatedRecord: 'ST-000185',
    });
  }

  let finalDecision = null;
  let businessRecordOutcome = null;
  let finalisation = emptyFinalisation();
  let complianceDecisionId = null;
  let archiveHandoffSucceeded = false;
  let reviewChecks = buildDefaultReviewChecks(DEFAULT_DECISION_CHECK_LABELS, { completed: decided });

  if (decided) {
    const decisionId = decisionIdFromCaseId(id);
    const newRecordId = recordIdFromCaseId(id);
    const action =
      caseType === 'New' ? 'Create Record' : 'Replace Record';
    complianceDecisionId = decisionId;
    finalDecision = {
      outcome: 'Approved',
      decidedBy: supportTeamMember !== 'Unassigned' ? supportTeamMember : 'Jordan Lee',
      decidedOn: lastUpdated,
      rejectionBasis: [],
      rejectionNotes: '',
    };
    businessRecordOutcome = {
      action,
      recordId: newRecordId,
      recordStatus: 'Active',
      effectiveFrom: lastUpdated,
      expiryDate: expiryApplies ? '2026-03-12' : null,
      previousRecord: currentRecord?.businessComplianceRecordId || '—',
    };
    finalisation = [
      {
        item: 'Business Record Action',
        result: action,
        relatedRecord: newRecordId,
        lastAttempt: lastUpdated,
        completedOn: lastUpdated,
        status: 'Succeeded',
      },
      {
        item: 'Archive Handoff',
        result: '—',
        relatedRecord: '—',
        lastAttempt: lastUpdated,
        completedOn: null,
        status: 'Pending',
      },
    ];
    auditLog.push(
      {
        id: 'LOG-005',
        dateTime: lastUpdated,
        event: 'Final decision',
        details: `${finalDecision.decidedBy} recorded Approved (${decisionId}).`,
        relatedRecord: decisionId,
      },
      {
        id: 'LOG-006',
        dateTime: lastUpdated,
        event: 'Completion result',
        details: `Business record action ${action} succeeded.`,
        relatedRecord: newRecordId,
      }
    );
  }

  const notes =
    started
      ? [
          {
            id: 'NOTE-1',
            addedBy: supportTeamMember !== 'Unassigned' ? supportTeamMember : 'Jordan Lee',
            addedOn: lastUpdated,
            text: 'Initial review started. Checking issuing authority details.',
            additions: [],
          },
        ]
      : [];

  return {
    complianceCaseId: id,
    complianceDecisionId,
    complianceItem,
    businessName,
    businessId,
    abn,
    accountHolderLegalName,
    accountStatus,
    businessTradeCategory,
    caseType,
    caseStatus,
    supportTeamMember,
    submittedAt,
    lastUpdated,
    started,
    snapshot: {
      appliedRuleId: appliedRuleIdForSource(complianceItemId, complianceItem),
      sourceId: complianceItemId,
      complianceType: complianceType === 'Licensed Trade' ? 'Licence' : complianceType,
      complianceItem,
      tradeCategory: sourceTradeCategory,
      expiryApplies: expiryApplies ? 'Yes' : 'No',
    },
    currentRecord,
    linkedCases: {
      previousComplianceCase: isRenewalOrReplacement ? 'CMP-003981' : null,
      previousComplianceDecision: isRenewalOrReplacement ? 'CMP-DEC-3981' : null,
      relatedAppeal: null,
      relatedSupportTicket: infoRequested ? 'ST-000185' : null,
    },
    submission: {
      submittedBy: businessName,
      submittedAt,
      submissionCount: 1,
      latestSubmission: 'Initial Submission',
      fields: submissionFields,
    },
    submittedForm: {
      name: `${String(complianceItem).toLowerCase().replace(/\s+/g, '_')}_form.pdf`,
      type: 'PDF',
    },
    supportingFiles: [
      {
        name: `${String(complianceItem).toLowerCase().replace(/\s+/g, '_')}_front.pdf`,
      },
      {
        name: `${String(complianceItem).toLowerCase().replace(/\s+/g, '_')}_back.jpg`,
      },
    ],
    submissionHistory: [
      {
        name: complianceItem,
        submittedBy: businessName,
        submittedOn: submittedAt,
        status: 'Submitted',
      },
    ],
    reviewChecks,
    caseActions: DEFAULT_CASE_ACTIONS,
    informationRequest: infoRequested
      ? {
          content: 'Please provide a clearer copy of the submitted document and confirm the holder name matches the business account.',
          responsePeriodDays,
          availableAt: infoAvailableAt,
          dueAt: infoDueAt,
          timeZone: 'Australia/Melbourne',
        }
      : null,
    auditLog,
    internalNotes: notes,
    finalDecision,
    businessRecordOutcome,
    finalisation,
    archiveHandoffSucceeded,
    finalisedBy: decided ? (supportTeamMember !== 'Unassigned' ? supportTeamMember : 'Jordan Lee') : null,
    finalisedOn: decided ? lastUpdated : null,
  };
};

const VerificationComplianceDetail = () => {
  const { complianceId: complianceIdParam } = useParams();
  const complianceId = complianceIdParam ? decodeURIComponent(complianceIdParam) : '';
  const navigate = useNavigate();
  const location = useLocation();

  const [activeTab, setActiveTab] = useState('overview');
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [auditLogCurrentPage, setAuditLogCurrentPage] = useState(1);
  const [activeReviewAction, setActiveReviewAction] = useState(null);
  const [resubmissionForm, setResubmissionForm] = useState({ informationRequested: '' });
  const [rejectForm, setRejectForm] = useState({ rejectionBases: [], rejectionNotes: '' });
  const [noteEditor, setNoteEditor] = useState(null);
  const [transferConfirmOpen, setTransferConfirmOpen] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await new Promise((resolve) => setTimeout(resolve, 120));
      const queueDocument = findQueueCase(complianceId) || location.state?.document || null;
      const session = getComplianceCaseOverride(complianceId);
      // Opening a case is view only — assignment happens via Assign to Me or manager Assign/Reassign.
      const detail = buildCaseDetail(complianceId, {
        ...queueDocument,
        ...(session?.status
          ? { case_status: session.status, verification_status: session.status }
          : {}),
        ...(session?.supportTeamMember || session?.support_team_member
          ? {
              support_team_member:
                session.supportTeamMember || session.support_team_member,
              assigned_to: session.supportTeamMember || session.support_team_member,
            }
          : {}),
        ...(session?.lastUpdated ? { last_updated: session.lastUpdated } : {}),
        ...(session?.informationRequest ? { information_request: true } : {}),
      });

      setCaseData(detail);
      setActiveReviewAction(null);
      setTransferConfirmOpen(false);
      setLoading(false);
    };
    load();
  }, [complianceId, location.state]);

  useEffect(() => {
    setAuditLogCurrentPage(1);
  }, [complianceId, activeTab]);

  const assignedDisplay = caseData
    ? formatAssignedDisplay(caseData.supportTeamMember)
    : 'Unassigned';
  const finalDecisionRecorded = Boolean(caseData?.finalDecision?.outcome);
  const duringReview = Boolean(caseData) && isDuringReview({
    case_status: caseData.caseStatus,
    support_team_member: caseData.supportTeamMember,
    finalDecision: caseData.finalDecision,
  });
  const isNewUnassigned =
    Boolean(caseData) &&
    caseData.caseStatus === 'New' &&
    assignedDisplay === 'Unassigned' &&
    !finalDecisionRecorded;
  const notesEditable = duringReview;
  const decisionActionsAvailable = duringReview;
  const responsePeriodDays = caseData?.caseActions?.askMoreInfo?.responsePeriodDays || 14;

  const persistQueueSnapshot = (detail, extra = {}) => {
    if (!detail?.complianceCaseId) return;
    const existing = findQueueCase(detail.complianceCaseId) || {};
    upsertQueueCase({
      ...existing,
      compliance_id: detail.complianceCaseId,
      case_status: detail.caseStatus,
      support_team_member:
        detail.supportTeamMember === 'Unassigned' ? null : detail.supportTeamMember,
      assigned_to: detail.supportTeamMember === 'Unassigned' ? null : detail.supportTeamMember,
      last_updated: detail.lastUpdated,
      case_type: detail.caseType,
      compliance_type: detail.snapshot?.complianceType,
      compliance_item: detail.complianceItem,
      compliance_item_id: detail.snapshot?.sourceId,
      trade_category: detail.snapshot?.tradeCategory,
      business_name: detail.businessName,
      business_id: detail.businessId,
      business_trade_category: detail.businessTradeCategory,
      submitted_at: detail.submittedAt,
      finalDecision: detail.finalDecision,
      businessRecordOutcome: detail.businessRecordOutcome,
      complianceDecisionId: detail.complianceDecisionId,
      currentRecord: detail.currentRecord,
      auditLog: detail.auditLog,
      information_request: Boolean(detail.informationRequest),
      ...extra,
    });
  };

  const handleAssignToMe = () => {
    if (!caseData || !isNewUnassigned) return;
    const now = new Date().toISOString();
    const next = {
      ...caseData,
      caseStatus: 'Under Review',
      supportTeamMember: CURRENT_STAFF_MEMBER,
      lastUpdated: now,
      started: true,
      auditLog: [
        ...(caseData.auditLog || []),
        {
          id: `LOG-ASSIGN-${Date.now()}`,
          dateTime: now,
          event: 'Assigned',
          details: `${CURRENT_STAFF_MEMBER} assigned the case to themselves; New → Under Review`,
          relatedRecord: '—',
        },
      ],
    };
    setCaseData(next);
    patchComplianceCaseOverride(caseData.complianceCaseId, {
      status: 'Under Review',
      supportTeamMember: CURRENT_STAFF_MEMBER,
      lastUpdated: now,
    });
    persistQueueSnapshot(next);
  };

  const auditRows = useMemo(() => {
    if (!caseData?.auditLog) return [];
    return caseData.auditLog
      .slice()
      .sort((a, b) => new Date(a.dateTime) - new Date(b.dateTime));
  }, [caseData]);

  const auditPagination = getDetailTablePagination(auditRows.length, auditLogCurrentPage);
  const paginatedAuditRows = auditPagination.paginate(auditRows);

  const sortedNotes = useMemo(
    () =>
      [...(caseData?.internalNotes || [])].sort(
        (a, b) => new Date(b.addedOn) - new Date(a.addedOn)
      ),
    [caseData?.internalNotes]
  );

  const appendAudit = (entry) => {
    const caseRef = caseData?.complianceCaseId || caseData?.caseId;
    if (caseRef && entry?.event) {
      try {
        const stamp = entry.dateTime || new Date().toISOString();
        recordCaseActivity({
          caseRef,
          module: 'Compliance',
          sourceModuleRef: 'compliance',
          changeType: entry.event,
          activityDetails: entry.details || entry.event,
          relatedRecord: entry.relatedRecord || caseRef,
          decisionRef: entry.decisionRef || null,
          changedOnIso: stamp,
          sourceChangeRef: `compliance::${caseRef}::${entry.event}::${stamp}`,
        });
      } catch {
        /* history recording failure must not reverse the source save */
      }
    }
    setCaseData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        lastUpdated: entry.dateTime,
        auditLog: [
          ...prev.auditLog,
          {
            id: `LOG-${Date.now()}`,
            relatedRecord: '—',
            ...entry,
          },
        ],
      };
    });
  };

  const toggleReviewCheck = (checkId) => {
    if (!decisionActionsAvailable) return;
    setCaseData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        lastUpdated: new Date().toISOString(),
        reviewChecks: prev.reviewChecks.map((c) =>
          c.id === checkId ? { ...c, checked: !c.checked } : c
        ),
      };
    });
  };

  const mandatoryChecksComplete = caseData?.reviewChecks
    ?.filter((c) => c.mandatory)
    .every((c) => c.checked);

  const completeBusinessRecordAction = (outcome, decidedOn) => {
    const action = outcome === 'Approved'
      ? caseData.caseType === 'New'
        ? 'Create Record'
        : 'Replace Record'
      : caseData.currentRecord
        ? 'Keep Current Record'
        : 'No Record';
    const relatedRecord =
      outcome === 'Approved'
        ? recordIdFromCaseId(caseData.complianceCaseId)
        : caseData.currentRecord?.businessComplianceRecordId || '—';
    return {
      action,
      recordId: outcome === 'Approved' ? relatedRecord : caseData.currentRecord?.businessComplianceRecordId || '—',
      recordStatus: outcome === 'Approved' ? 'Active' : caseData.currentRecord?.recordStatus || 'No Record',
      effectiveFrom: outcome === 'Approved' ? decidedOn : caseData.currentRecord?.effectiveFrom || null,
      expiryDate:
        outcome === 'Approved'
          ? caseData.snapshot.expiryApplies === 'Yes'
            ? '2026-03-12'
            : null
          : caseData.currentRecord?.expiryDate || null,
      previousRecord: caseData.currentRecord?.businessComplianceRecordId || '—',
      finalisation: [
        {
          item: 'Business Record Action',
          result: action,
          relatedRecord,
          lastAttempt: decidedOn,
          completedOn: decidedOn,
          status: 'Succeeded',
        },
        {
          item: 'Archive Handoff',
          result: '—',
          relatedRecord: '—',
          lastAttempt: decidedOn,
          completedOn: null,
          status: 'Pending',
        },
      ],
    };
  };

  const finalizeAndHandoff = (outcome, extras = {}) => {
    const now = new Date().toISOString();
    const decisionId = decisionIdFromCaseId(caseData.complianceCaseId);
    const recorded = completeBusinessRecordAction(outcome, now);
    const next = {
      ...caseData,
      caseStatus: 'Decided',
      complianceDecisionId: decisionId,
      lastUpdated: now,
      finalDecision: {
        outcome,
        decidedBy: assignedDisplay,
        decidedOn: now,
        rejectionBasis: extras.rejectionBasis || [],
        rejectionNotes: extras.rejectionNotes || '',
      },
      businessRecordOutcome: {
        action: recorded.action,
        recordId: recorded.recordId,
        recordStatus: recorded.recordStatus,
        effectiveFrom: recorded.effectiveFrom,
        expiryDate: recorded.expiryDate,
        previousRecord: recorded.previousRecord,
      },
      finalisation: [
        {
          item: 'Business Record Action',
          result: recorded.action,
          relatedRecord: recorded.recordId,
          lastAttempt: now,
          completedOn: now,
          status: 'Succeeded',
        },
        {
          item: 'Archive Handoff',
          result: 'Handed off',
          relatedRecord: caseData.complianceCaseId,
          lastAttempt: now,
          completedOn: now,
          status: 'Succeeded',
        },
      ],
      finalisedBy: assignedDisplay,
      finalisedOn: now,
      archiveHandoffSucceeded: true,
      currentRecord:
        recorded.action === 'Create Record' || recorded.action === 'Replace Record'
          ? {
              businessComplianceRecordId: recorded.recordId,
              recordStatus: recorded.recordStatus,
              effectiveFrom: recorded.effectiveFrom,
              expiryDate: recorded.expiryDate,
            }
          : caseData.currentRecord,
      auditLog: [
        ...(caseData.auditLog || []),
        {
          id: `LOG-DEC-${Date.now()}`,
          dateTime: now,
          event: 'Final decision',
          details: extras.decisionDetails || `${assignedDisplay} recorded ${outcome} (${decisionId}).`,
          relatedRecord: decisionId,
        },
        {
          id: `LOG-COMP-${Date.now() + 1}`,
          dateTime: now,
          event: 'Completion result',
          details: `Business record action ${recorded.action} succeeded.`,
          relatedRecord: recorded.recordId,
        },
        {
          id: `LOG-ARCH-${Date.now() + 2}`,
          dateTime: now,
          event: 'Moved to Archive',
          details: `System; Lifecycle Control; ${caseData.complianceCaseId}`,
          relatedRecord: caseData.complianceCaseId,
        },
      ],
    };
    setCaseData(next);
    persistQueueSnapshot(next, { recordStatus: recorded.recordStatus });
    handoffQueueCaseToArchive(next);
    markQueueHandoffSuccess(caseData.complianceCaseId);
    patchComplianceCaseOverride(caseData.complianceCaseId, {
      status: 'Decided',
      lastUpdated: now,
    });
    setActiveReviewAction(null);
    navigate(COMPLIANCE_QUEUE_ROUTE);
  };

  const handleApprove = () => {
    if (!decisionActionsAvailable) return;
    if (!mandatoryChecksComplete) {
      alert('Approval requires all Mandatory approval checks to be completed.');
      return;
    }
    if (
      (caseData.caseType === 'Renewal' || caseData.caseType === 'Replacement') &&
      !caseData.currentRecord
    ) {
      alert('A Renewal or Replacement without its required existing record cannot be approved as New.');
      return;
    }
    finalizeAndHandoff('Approved');
  };

  const handleReject = () => {
    if (!decisionActionsAvailable) return;
    if (!rejectForm.rejectionBases.length) {
      alert('Please select at least one Rejection Basis.');
      return;
    }
    const basisSummary = rejectForm.rejectionBases.join(', ');
    const decisionId = decisionIdFromCaseId(caseData.complianceCaseId);
    finalizeAndHandoff('Rejected', {
      rejectionBasis: rejectForm.rejectionBases,
      rejectionNotes: rejectForm.rejectionNotes.trim(),
      decisionDetails: `${assignedDisplay} recorded Rejected (${decisionId}). Rejection Basis: ${basisSummary}.`,
    });
    setRejectForm({ rejectionBases: [], rejectionNotes: '' });
  };

  const toggleRejectionBasis = (basis) => {
    setRejectForm((prev) => {
      const selected = prev.rejectionBases.includes(basis)
        ? prev.rejectionBases.filter((item) => item !== basis)
        : [...prev.rejectionBases, basis];
      return { ...prev, rejectionBases: selected };
    });
  };

  const handleTransfer = () => {
    if (!decisionActionsAvailable) return;
    const now = new Date().toISOString();
    // Transfer creates no Compliance Decision ID and no Archive entry.
    markQueueTransferred(caseData.complianceCaseId);
    patchComplianceCaseOverride(caseData.complianceCaseId, {
      status: 'Transferred',
      lastUpdated: now,
    });
    appendAudit({
      dateTime: now,
      event: 'Transfer to Fraud & Security',
      details: `${assignedDisplay} transferred as a new Fraud & Security case (Case Transfer form). No Compliance Decision ID.`,
      relatedRecord: '—',
    });
    setTransferConfirmOpen(false);
    setActiveReviewAction(null);
    navigate(COMPLIANCE_QUEUE_ROUTE);
  };

  const handleAskMoreInfo = () => {
    if (!decisionActionsAvailable) return;
    if (!resubmissionForm.informationRequested.trim()) {
      alert('Please describe the information needed.');
      return;
    }
    const now = new Date().toISOString();
    const dueAt = addCalendarDays(now, responsePeriodDays);
    // Each distinct Ask More Info creates one linked Support Ticket (General Enquiry / STC-001).
    // Reuse an existing linked ticket id only when the same accepted request is redelivered.
    const existingTicketId = caseData.linkedCases?.relatedSupportTicket;
    const ticketId =
      existingTicketId && caseData.informationRequest?.content === resubmissionForm.informationRequested.trim()
        ? existingTicketId
        : existingTicketId || 'ST-000185';
    const informationRequested = resubmissionForm.informationRequested.trim();
    // Info requests keep Under Review — there is no Awaiting Information status.
    patchComplianceCaseOverride(caseData.complianceCaseId, {
      status: 'Under Review',
      supportTeamMember: caseData.supportTeamMember,
      lastUpdated: now,
      informationRequest: true,
    });
    setCaseData((prev) => ({
      ...prev,
      caseStatus: 'Under Review',
      lastUpdated: now,
      linkedCases: {
        ...prev.linkedCases,
        relatedSupportTicket: ticketId,
      },
      informationRequest: {
        content: informationRequested,
        responsePeriodDays,
        availableAt: now,
        dueAt,
        timeZone: recordedTimeZone(),
      },
      informationRequestHistory: [
        ...(prev.informationRequestHistory || []),
        {
          ticketId,
          content: informationRequested,
          availableAt: now,
          dueAt,
          responsePeriodDays,
          submittedBy: assignedDisplay,
        },
      ],
    }));
    appendAudit({
      dateTime: now,
      event: 'Information requested',
      details: `${assignedDisplay} requested more information. Linked Support Ticket ${ticketId}. Due ${formatDateTime(dueAt)}.`,
      relatedRecord: ticketId,
    });
    persistQueueSnapshot({
      ...caseData,
      caseStatus: 'Under Review',
      lastUpdated: now,
      informationRequest: {
        content: informationRequested,
        responsePeriodDays,
        availableAt: now,
        dueAt,
        timeZone: recordedTimeZone(),
      },
    });
    setActiveReviewAction(null);
    setResubmissionForm({ informationRequested: '' });
  };

  const saveNote = () => {
    if (!notesEditable || !noteEditor) return;
    const text = String(noteEditor.draft || '').trim();
    if (!text) return;
    const now = new Date().toISOString();
    if (noteEditor.mode === 'add') {
      const note = {
        id: `NOTE-${Date.now()}`,
        addedBy: assignedDisplay,
        addedOn: now,
        text,
        additions: [],
      };
      setCaseData((prev) => ({
        ...prev,
        lastUpdated: now,
        internalNotes: [...(prev.internalNotes || []), note],
      }));
      appendAudit({
        dateTime: now,
        event: 'Note added',
        details: `${assignedDisplay} saved an internal note.`,
      });
    } else if (noteEditor.mode === 'edit' && noteEditor.noteId) {
      setCaseData((prev) => ({
        ...prev,
        lastUpdated: now,
        internalNotes: (prev.internalNotes || []).map((note) =>
          note.id === noteEditor.noteId
            ? {
                ...note,
                text,
                additions: [],
              }
            : note
        ),
      }));
      appendAudit({
        dateTime: now,
        event: 'Note updated',
        details: `${assignedDisplay} edited an internal note.`,
      });
    }
    setNoteEditor(null);
  };

  const openLinked = (id, previousCaseId) => {
    const path = relatedRecordPath(id, previousCaseId);
    if (path) navigate(path);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <AdminSupportingText>Loading compliance case...</AdminSupportingText>
        </div>
      </AdminLayout>
    );
  }

  if (!caseData) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <AdminAlertText>Compliance case not found</AdminAlertText>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="tp-container-main">
        <div>
          <DashboardMainContainer className={ADMIN_DETAIL_MAIN_HEADER_CLASS}>
            <AdminPanelTitle className="text-offblack">
              {caseData.complianceItem}
            </AdminPanelTitle>
          </DashboardMainContainer>

          <DashboardMainContainer className="!bg-white">
            <div className="border-b border-lightgray">
              <div className="flex space-x-6 overflow-x-auto">
                {TABS.map((tab) => (
                  <AdminTabButton
                    key={tab.id}
                    active={activeTab === tab.id}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    {tab.label}
                  </AdminTabButton>
                ))}
              </div>
            </div>

            <div className={adminDetailTabContentClassName(activeTab)}>
              {activeTab === 'overview' && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-6">
                  <div className="space-y-4">
                    <DetailField label="Compliance Case ID" value={caseData.complianceCaseId} />
                    <DetailField
                      label="Compliance Decision ID"
                      value={caseData.complianceDecisionId}
                    />
                    <DetailField label="Case Type" value={caseData.caseType} />
                    <DetailField label="Case Status" value={caseData.caseStatus} />
                    <DetailField
                      label="Support Team Member"
                      value={assignedDisplay === 'Unassigned' ? '—' : assignedDisplay}
                    />
                    <DetailField label="Submitted" value={formatDateTime(caseData.submittedAt)} />
                    <DetailField label="Last Updated" value={formatDateTime(caseData.lastUpdated)} />
                  </div>

                  <div className="space-y-4">
                    <DetailField label="Business Name" value={caseData.businessName} />
                    <DetailField label="Business ID" value={caseData.businessId} />
                    <DetailField label="ABN" value={caseData.abn} />
                    <DetailField
                      label="Account Holder Legal Name"
                      value={caseData.accountHolderLegalName}
                    />
                    <DetailField label="Trade Category" value={caseData.businessTradeCategory} />
                    <DetailField label="Account Status" value={caseData.accountStatus} />
                  </div>

                  <div className="space-y-4">
                    <DetailField
                      label="Applied Compliance Rule ID"
                      value={caseData.snapshot.appliedRuleId}
                    />
                    <DetailField label="Source ID" value={caseData.snapshot.sourceId} />
                    <DetailField label="Type" value={caseData.snapshot.complianceType} />
                    <DetailField label="Name" value={caseData.snapshot.complianceItem} />
                    <DetailField label="Trade Category" value={caseData.snapshot.tradeCategory} />
                  </div>

                  <div className="space-y-4">
                    <DetailField
                      label="Business Compliance Record ID"
                      value={caseData.currentRecord?.businessComplianceRecordId}
                    />
                    <DetailField
                      label="Record Status"
                      value={caseData.currentRecord?.recordStatus}
                    />
                    <DetailField
                      label="Effective From"
                      value={
                        caseData.currentRecord?.effectiveFrom
                          ? formatDate(caseData.currentRecord.effectiveFrom)
                          : null
                      }
                    />
                    <DetailField
                      label="Expiry Date"
                      value={
                        caseData.currentRecord?.expiryDate
                          ? formatDate(caseData.currentRecord.expiryDate)
                          : null
                      }
                    />
                    <LinkedIdField
                      label="Previous Compliance Case ID"
                      value={caseData.linkedCases?.previousComplianceCase}
                      onOpen={(id) => openLinked(id)}
                    />
                    <LinkedIdField
                      label="Previous Compliance Decision ID"
                      value={caseData.linkedCases?.previousComplianceDecision}
                      onOpen={
                        caseData.linkedCases?.previousComplianceCase
                          ? (id) =>
                              openLinked(id, caseData.linkedCases.previousComplianceCase)
                          : undefined
                      }
                    />
                    <LinkedIdField
                      label="Related Appeal ID"
                      value={caseData.linkedCases?.relatedAppeal}
                      onOpen={(id) => openLinked(id)}
                    />
                    <LinkedIdField
                      label="Related Support Ticket ID"
                      value={caseData.linkedCases?.relatedSupportTicket}
                      onOpen={(id) => openLinked(id)}
                    />
                  </div>
                </div>
              )}

              {activeTab === 'submission' && (
                <div className="space-y-8">
                  <div>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-8 gap-y-4">
                      <DetailField label="Submitted By" value={caseData.submission.submittedBy} />
                      <DetailField
                        label="Submitted On"
                        value={formatDateTime(caseData.submission.submittedAt)}
                      />
                      <DetailField
                        label="Latest Submission"
                        value={caseData.submission.latestSubmission}
                      />
                      <DetailField
                        label="Submission Count"
                        value={String(caseData.submission.submissionCount ?? 1)}
                      />
                    </div>
                  </div>

                  <div>
                    <AdminPanelTitle className="mb-4">Submitted Application</AdminPanelTitle>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-8 gap-y-4">
                      {caseData.submission.fields.map((field) => (
                        <DetailField key={field.label} label={field.label} value={field.value} />
                      ))}
                    </div>
                  </div>

                  {caseData.submittedForm ? (
                    <div>
                      <AdminPanelTitle className="mb-4">Submitted Form</AdminPanelTitle>
                      <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
                        <div
                          className="grid w-full max-w-full min-w-0 py-3 px-4 text-offblack border-b border-lightgray gap-4"
                          style={{ gridTemplateColumns: FILE_ACTION_GRID }}
                        >
                          <div className="flex items-center min-w-0">
                            <AdminTableHeader>File</AdminTableHeader>
                          </div>
                          <div className="flex items-center justify-center min-w-0">
                            <AdminTableHeader>View</AdminTableHeader>
                          </div>
                        </div>
                        <div
                          className="grid w-full max-w-full min-w-0 py-3 px-4 text-offblack gap-4 items-center"
                          style={{ gridTemplateColumns: FILE_ACTION_GRID }}
                        >
                          <div className="flex items-center min-w-0">
                            <AdminTableBody className="truncate" title={caseData.submittedForm.name}>
                              {caseData.submittedForm.name}
                            </AdminTableBody>
                          </div>
                          <div className="flex items-center justify-center min-w-0">
                            <AdminLinkAction
                              type="button"
                              onClick={() => alert(`View: ${caseData.submittedForm.name}`)}
                            >
                              View
                            </AdminLinkAction>
                          </div>
                        </div>
                      </DashboardMainContainer>
                    </div>
                  ) : null}

                  <div>
                    <AdminPanelTitle className="mb-4">Supporting Files</AdminPanelTitle>
                    <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
                      <div
                        className="grid w-full max-w-full min-w-0 py-3 px-4 text-offblack border-b border-lightgray gap-4"
                        style={{ gridTemplateColumns: FILE_ACTION_GRID }}
                      >
                        <div className="flex items-center min-w-0">
                          <AdminTableHeader>File</AdminTableHeader>
                        </div>
                        <div className="flex items-center justify-center min-w-0">
                          <AdminTableHeader>View</AdminTableHeader>
                        </div>
                      </div>
                      {caseData.supportingFiles.map((file) => (
                        <div
                          key={file.name}
                          className="grid w-full max-w-full min-w-0 py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-4 items-center"
                          style={{ gridTemplateColumns: FILE_ACTION_GRID }}
                        >
                          <div className="flex items-center min-w-0">
                            <AdminTableBody className="truncate" title={file.name}>
                              {file.name}
                            </AdminTableBody>
                          </div>
                          <div className="flex items-center justify-center min-w-0">
                            <AdminLinkAction
                              type="button"
                              onClick={() => alert(`View: ${file.name}`)}
                            >
                              View
                            </AdminLinkAction>
                          </div>
                        </div>
                      ))}
                    </DashboardMainContainer>
                  </div>

                  <div>
                    <AdminPanelTitle className="mb-4">Submission History</AdminPanelTitle>
                    <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
                      <div
                        className="grid py-3 px-4 text-offblack border-b border-lightgray gap-4"
                        style={{ gridTemplateColumns: SUBMISSION_HISTORY_GRID }}
                      >
                        <div className="flex items-center min-w-0">
                          <AdminTableHeader>Name</AdminTableHeader>
                        </div>
                        <div className="flex items-center min-w-0">
                          <AdminTableHeader>Submitted By</AdminTableHeader>
                        </div>
                        <div className="flex items-center min-w-0">
                          <AdminTableHeader>Submitted On</AdminTableHeader>
                        </div>
                        <div className="flex items-center min-w-0">
                          <AdminTableHeader>Status</AdminTableHeader>
                        </div>
                        <div className="flex items-center justify-center min-w-0">
                          <AdminTableHeader>Action</AdminTableHeader>
                        </div>
                      </div>
                      {caseData.submissionHistory.map((row) => (
                        <div
                          key={`${row.name}-${row.submittedOn}`}
                          className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-4 items-center"
                          style={{ gridTemplateColumns: SUBMISSION_HISTORY_GRID }}
                        >
                          <div className="flex items-center min-w-0">
                            <AdminTableBody className="truncate">{row.name}</AdminTableBody>
                          </div>
                          <div className="flex items-center min-w-0">
                            <AdminTableBody className="truncate">{row.submittedBy}</AdminTableBody>
                          </div>
                          <div className="flex items-center min-w-0">
                            <AdminTableMeta>{formatDateTime(row.submittedOn)}</AdminTableMeta>
                          </div>
                          <div className="flex items-center min-w-0">
                            <AdminTableBody className="truncate">{row.status}</AdminTableBody>
                          </div>
                          <div className="flex items-center justify-center min-w-0">
                            <AdminLinkAction
                              type="button"
                              onClick={() => alert(`View submission: ${row.name}`)}
                            >
                              View
                            </AdminLinkAction>
                          </div>
                        </div>
                      ))}
                    </DashboardMainContainer>
                  </div>
                </div>
              )}

              {activeTab === 'review' && (
                isNewUnassigned ? (
                  <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
                    <div className="space-y-4">
                      <AdminSupportingText className="text-offblack">
                        Opening a case is view only. Press Assign to Me to take this case and move it to Under
                        Review.
                      </AdminSupportingText>
                      <div className="flex flex-wrap justify-start gap-3">
                        <AdminHeaderAction type="button" onClick={handleAssignToMe}>
                          Assign to Me
                        </AdminHeaderAction>
                      </div>
                    </div>
                  </DashboardMainContainer>
                ) : (
                <div className="space-y-8">
                  <div>
                    <AdminPanelTitle className="mb-4">Case Checklist</AdminPanelTitle>
                    <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
                      <div className="space-y-3">
                        {caseData.reviewChecks.map((check) => (
                          <label
                            key={check.id}
                            className={`flex items-start gap-2 ${
                              decisionActionsAvailable
                                ? 'cursor-pointer'
                                : 'cursor-not-allowed opacity-60'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={check.checked}
                              disabled={!decisionActionsAvailable}
                              onChange={() => toggleReviewCheck(check.id)}
                              className="w-4 h-4 mt-0.5 text-primaryblue shrink-0"
                            />
                            <span className="text-offblack text-sm">{check.label}</span>
                          </label>
                        ))}
                      </div>
                    </DashboardMainContainer>
                  </div>

                  {finalDecisionRecorded ? (
                    <div className="space-y-4">
                      <DetailField
                        label="Compliance Decision ID"
                        value={caseData.complianceDecisionId}
                      />
                      <DetailField
                        label="Final Outcome"
                        value={caseData.finalDecision.outcome}
                      />
                      <DetailField label="Decided By" value={caseData.finalDecision.decidedBy} />
                      <DetailField
                        label="Decided On"
                        value={formatDateTime(caseData.finalDecision.decidedOn)}
                      />
                      {caseData.finalDecision.outcome === 'Rejected' ? (
                        <>
                          <DetailField
                            label="Rejection Basis"
                            value={
                              (caseData.finalDecision.rejectionBasis || []).join(', ') || '—'
                            }
                          />
                          <DetailField
                            label="Rejection Notes"
                            value={caseData.finalDecision.rejectionNotes}
                          />
                        </>
                      ) : null}
                    </div>
                  ) : (
                    <>
                      <div className="flex flex-wrap gap-3">
                        <AdminFormSecondaryAction
                          type="button"
                          disabled={!decisionActionsAvailable}
                          onClick={() => {
                            const now = new Date().toISOString();
                            setCaseData((prev) => ({ ...prev, lastUpdated: now }));
                            persistQueueSnapshot({ ...caseData, lastUpdated: now });
                            appendAudit({
                              dateTime: now,
                              event: 'Progress saved',
                              details: `${assignedDisplay} saved checklist and draft notes.`,
                              relatedRecord: '—',
                            });
                          }}
                        >
                          Save
                        </AdminFormSecondaryAction>
                        <AdminHeaderAction
                          type="button"
                          disabled={!decisionActionsAvailable}
                          onClick={handleApprove}
                        >
                          Approve
                        </AdminHeaderAction>
                        <AdminFormSecondaryAction
                          type="button"
                          disabled={!decisionActionsAvailable}
                          onClick={() =>
                            setActiveReviewAction(activeReviewAction === 'reject' ? null : 'reject')
                          }
                        >
                          Not Approve
                        </AdminFormSecondaryAction>
                        <AdminFormSecondaryAction
                          type="button"
                          disabled={!decisionActionsAvailable}
                          onClick={() =>
                            setActiveReviewAction(
                              activeReviewAction === 'resubmission' ? null : 'resubmission'
                            )
                          }
                        >
                          Ask More Info
                        </AdminFormSecondaryAction>
                        <AdminHeaderAction
                          type="button"
                          disabled={!decisionActionsAvailable}
                          onClick={() => setTransferConfirmOpen(true)}
                        >
                          Transfer
                        </AdminHeaderAction>
                      </div>

                      {activeReviewAction === 'resubmission' ? (
                        <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
                          <div className="space-y-4">
                            <AdminPanelTitle>Ask More Info</AdminPanelTitle>
                            <div>
                              <AdminDetailFieldLabel className="block mb-1.5">
                                Information Requested
                              </AdminDetailFieldLabel>
                              <textarea
                                value={resubmissionForm.informationRequested}
                                onChange={(e) =>
                                  setResubmissionForm({ informationRequested: e.target.value })
                                }
                                rows={3}
                                className={`${adminFormTextareaClasses()} w-full min-h-[100px]`}
                              />
                            </div>
                            <DetailField
                              label="Response Period"
                              value={`${responsePeriodDays} calendar days`}
                            />
                            <DetailField label="Due Date" value="—" />
                            <div className="flex justify-end gap-3">
                              <AdminFormSecondaryAction
                                type="button"
                                onClick={() => setActiveReviewAction(null)}
                              >
                                Cancel
                              </AdminFormSecondaryAction>
                              <AdminHeaderAction type="button" onClick={handleAskMoreInfo}>
                                Submit
                              </AdminHeaderAction>
                            </div>
                          </div>
                        </DashboardMainContainer>
                      ) : null}

                      {activeReviewAction === 'reject' ? (
                        <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
                          <div className="space-y-4">
                            <AdminPanelTitle>Not Approve</AdminPanelTitle>
                            <div>
                              <AdminDetailFieldLabel className="block mb-1.5">
                                Rejection Basis
                              </AdminDetailFieldLabel>
                              <div className="space-y-2">
                                {QUEUE_REJECTION_BASIS.map((basis) => (
                                  <label key={basis} className="flex items-start gap-2 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={rejectForm.rejectionBases.includes(basis)}
                                      onChange={() => toggleRejectionBasis(basis)}
                                      className="w-4 h-4 mt-0.5 text-primaryblue shrink-0"
                                    />
                                    <span className="text-offblack text-sm">{basis}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                            <div>
                              <AdminDetailFieldLabel className="block mb-1.5">
                                Rejection Notes
                              </AdminDetailFieldLabel>
                              <textarea
                                value={rejectForm.rejectionNotes}
                                onChange={(e) =>
                                  setRejectForm((prev) => ({
                                    ...prev,
                                    rejectionNotes: e.target.value,
                                  }))
                                }
                                rows={3}
                                className={`${adminFormTextareaClasses()} w-full min-h-[100px]`}
                              />
                            </div>
                            <div className="flex justify-end gap-3">
                              <AdminFormSecondaryAction
                                type="button"
                                onClick={() => setActiveReviewAction(null)}
                              >
                                Cancel
                              </AdminFormSecondaryAction>
                              <AdminHeaderAction type="button" onClick={handleReject}>
                                Submit Not Approve
                              </AdminHeaderAction>
                            </div>
                          </div>
                        </DashboardMainContainer>
                      ) : null}
                    </>
                  )}
                </div>
                )
              )}

              {activeTab === 'notes' && (
                <div>
                  {notesEditable ? (
                    <div className="mb-4 flex justify-end">
                      <AdminHeaderAction
                        type="button"
                        onClick={() => setNoteEditor({ mode: 'add', draft: '' })}
                      >
                        Add Note
                      </AdminHeaderAction>
                    </div>
                  ) : null}
                  <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
                    <div
                      className="grid py-3 text-offblack border-b border-lightgray px-4 gap-4"
                      style={{ gridTemplateColumns: NOTES_TABLE_GRID }}
                    >
                      <div className="flex items-center min-w-0">
                        <AdminTableHeader>Note</AdminTableHeader>
                      </div>
                      <div className="flex items-center min-w-0">
                        <AdminTableHeader>Added By</AdminTableHeader>
                      </div>
                      <div className="flex items-center min-w-0">
                        <AdminTableHeader>Added On</AdminTableHeader>
                      </div>
                      <div className="flex items-center justify-center min-w-0">
                        <AdminTableHeader>Actions</AdminTableHeader>
                      </div>
                    </div>
                    {sortedNotes.length > 0 ? (
                      sortedNotes.map((note) => (
                        <div
                          key={note.id}
                          className="grid py-4 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-4"
                          style={{ gridTemplateColumns: NOTES_TABLE_GRID }}
                        >
                          <div className="flex items-start min-w-0">
                            <AdminTableBody className="whitespace-pre-wrap break-words">
                              {note.text}
                            </AdminTableBody>
                          </div>
                          <div className="flex items-start min-w-0">
                            <AdminTableBody emphasis>{note.addedBy}</AdminTableBody>
                          </div>
                          <div className="flex items-start min-w-0">
                            <AdminTableMeta>{formatDateTime(note.addedOn)}</AdminTableMeta>
                          </div>
                          <div className="flex items-start justify-center min-w-0">
                            {notesEditable ? (
                              <AdminLinkAction
                                type="button"
                                onClick={() =>
                                  setNoteEditor({
                                    mode: 'edit',
                                    noteId: note.id,
                                    draft: [note.text, ...(note.additions || []).map((addition) => addition.text)]
                                      .filter(Boolean)
                                      .join('\n\n'),
                                  })
                                }
                              >
                                Edit
                              </AdminLinkAction>
                            ) : null}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="py-8 text-center">
                        <AdminSupportingText className="text-mediumgray">No notes yet</AdminSupportingText>
                      </div>
                    )}
                  </DashboardMainContainer>
                </div>
              )}

              {activeTab === 'logs' && (
                <div className="min-w-0 max-w-full">
                  <DashboardMainContainer className={`${ADMIN_DETAIL_TABLE_CLASS} overflow-hidden`}>
                    <div className="w-full min-w-0 max-w-full overflow-x-auto">
                      <div
                        className="grid w-full max-w-full min-w-0 py-3 text-offblack border-b border-lightgray px-4 gap-x-2 sm:gap-x-3 gap-y-2"
                        style={{ gridTemplateColumns: AUDIT_LOG_GRID }}
                      >
                        <div className="flex items-center min-w-0">
                          <AdminTableHeader>Date and Time</AdminTableHeader>
                        </div>
                        <div className="flex items-center min-w-0">
                          <AdminTableHeader>Event</AdminTableHeader>
                        </div>
                        <div className="flex items-center min-w-0">
                          <AdminTableHeader>Details</AdminTableHeader>
                        </div>
                        <div className="flex items-center min-w-0">
                          <AdminTableHeader>Related Record</AdminTableHeader>
                        </div>
                      </div>
                      {paginatedAuditRows.length > 0 ? (
                        paginatedAuditRows.map((log, idx) => {
                          const related = log.relatedRecord && log.relatedRecord !== '—'
                            ? log.relatedRecord
                            : null;
                          const relatedPath = related
                            ? relatedRecordPath(
                                related,
                                caseData.linkedCases?.previousComplianceCase
                              )
                            : null;
                          return (
                            <div
                              key={`${log.id || log.dateTime}-${log.event}-${idx}`}
                              className="grid w-full max-w-full min-w-0 py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-x-2 sm:gap-x-3 gap-y-2 items-start"
                              style={{ gridTemplateColumns: AUDIT_LOG_GRID }}
                            >
                              <div className="flex items-start min-w-0">
                                <AdminTableMeta className="break-words">
                                  {formatDateTime(log.dateTime)}
                                </AdminTableMeta>
                              </div>
                              <div className="flex items-start min-w-0">
                                <AdminTableBody className="break-words">{log.event || '—'}</AdminTableBody>
                              </div>
                              <div className="flex items-start min-w-0">
                                <AdminTableBody className="break-words whitespace-pre-wrap">
                                  {log.details || '—'}
                                </AdminTableBody>
                              </div>
                              <div className="flex items-start min-w-0">
                                {related && relatedPath ? (
                                  <AdminLinkAction type="button" onClick={() => navigate(relatedPath)}>
                                    {related}
                                  </AdminLinkAction>
                                ) : (
                                  <AdminTableBody className="break-words">{related || '—'}</AdminTableBody>
                                )}
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="py-8 text-center">
                          <AdminSupportingText className="text-mediumgray">
                            No audit logs found
                          </AdminSupportingText>
                        </div>
                      )}
                    </div>
                  </DashboardMainContainer>
                  <ConfigurationListPaginationFooter
                    show={auditPagination.showPagination}
                    rangeStart={auditPagination.rangeStart}
                    rangeEnd={auditPagination.rangeEnd}
                    totalCount={auditRows.length}
                    currentPage={auditPagination.effectivePage}
                    totalPages={auditPagination.totalPages}
                    onPrev={() => setAuditLogCurrentPage((prev) => Math.max(1, prev - 1))}
                    onNext={() =>
                      setAuditLogCurrentPage((prev) => Math.min(auditPagination.totalPages, prev + 1))
                    }
                    goToPage={setAuditLogCurrentPage}
                  />
                </div>
              )}
            </div>
          </DashboardMainContainer>
        </div>
      </div>

      {noteEditor ? (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setNoteEditor(null)}
        >
          <div
            className={`bg-white rounded-xl p-6 ${ACTION_POPUP_CONTENT_WIDTH_CLASS} mx-4 shadow-lg border border-lightgray`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <AdminPanelTitle className="text-offblack">
                {noteEditor.mode === 'add' ? 'Add Note' : 'Edit'}
              </AdminPanelTitle>
              <button
                type="button"
                onClick={() => setNoteEditor(null)}
                className="text-offblack hover:text-primaryblue shrink-0"
                aria-label="Close"
              >
                <i className="fi fi-rr-cross text-base" />
              </button>
            </div>
            <textarea
              value={noteEditor.draft}
              onChange={(e) => setNoteEditor((prev) => ({ ...prev, draft: e.target.value }))}
              rows={4}
              className={`${adminFormTextareaClasses()} w-full min-h-[100px]`}
            />
            <div className="mt-6 flex justify-end gap-3">
              <AdminFormSecondaryAction type="button" onClick={() => setNoteEditor(null)}>
                Cancel
              </AdminFormSecondaryAction>
              <AdminHeaderAction type="button" onClick={saveNote}>
                Save
              </AdminHeaderAction>
            </div>
          </div>
        </div>
      ) : null}

      {transferConfirmOpen ? (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4"
          onClick={() => setTransferConfirmOpen(false)}
          role="presentation"
        >
          <div
            className={`bg-white rounded-xl p-6 ${ACTION_POPUP_CONTENT_WIDTH_CLASS} mx-4 shadow-lg border border-lightgray`}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="compliance-transfer-confirm-title"
          >
            <AdminPanelTitle
              id="compliance-transfer-confirm-title"
              className="text-offblack text-center"
            >
              Are you sure?
            </AdminPanelTitle>
            <AdminSupportingText className="text-offblack text-center mt-3">
              Are you sure you want to transfer this case to Fraud & Security?
            </AdminSupportingText>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <AdminFormSecondaryAction
                type="button"
                onClick={() => setTransferConfirmOpen(false)}
              >
                No
              </AdminFormSecondaryAction>
              <AdminHeaderAction type="button" onClick={handleTransfer}>
                Yes
              </AdminHeaderAction>
            </div>
          </div>
        </div>
      ) : null}

    </AdminLayout>
  );
};

export default VerificationComplianceDetail;
